<script setup>
// core
import {useTemplateRef} from "vue";
import {useInfiniteScroll} from "@vueuse/core";

// components
import AudioPlayer from "@/components/Recurring/AudioPlayer.vue";
import CallTypeView from "@/components/Telephony/CallTypeView.vue";
import CallNumberView from "@/components/Telephony/CallNumberView.vue";
import BaseFadeScroll from "@/components/ui/BaseFadeScroll.vue";

// composables
import useCallDate from "@/components/Telephony/api/useCallDate";
import useScrollFade from "@/components/Telephony/api/useScrollFade";
import useFillHeight from "@/components/Telephony/api/useFillHeight";

// constants
import {ANSWERED} from "@/components/Telephony/constants";

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    // a page is on its way: the loader row sits at the foot
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(["loadMore"]);
const scrollComponent = useTemplateRef('scrollComponent')
const {formatDate, formatTime, recordName} = useCallDate()
const {isBottom} = useScrollFade(scrollComponent, () => props.data?.length)
const {maxHeight} = useFillHeight(scrollComponent)

// the same paging as CallsHistoryTable
useInfiniteScroll(
    scrollComponent,
    () => {
        emit("loadMore");
    },
    {
        distance: 50,
        interval: 300,
    }
);

const getDuration = (item) => item.status === ANSWERED && item.duration ? item.duration : '—'
</script>

<template>
    <div class="calls-n">
        <div class="calls-n__head">
            <span
                v-for="(title, index) in $tm('telephony.calls_history.narrow_columns')"
                :key="index"
                class="headed_text"
            >{{ title }}</span>
        </div>

        <ul class="calls-n__list" ref="scrollComponent" :style="{ maxHeight }">
            <li v-for="(item, i) in props.data" :key="i" class="calls-n__row">
                <span class="calls-n__when">
                    <span class="calls-n__date">{{ formatDate(item.datetime) }}</span>
                    <span class="calls-n__sub">{{ formatTime(item.datetime) }}</span>
                </span>

                <CallTypeView :item="item"/>

                <!-- the client on top, our number under it; the bent arrow runs
                     down into ours for an incoming call and up from it for an outgoing one -->
                <span class="calls-n__pair">
                    <CallNumberView class="calls-n__client" :number="item.contact"/>
                    <span class="calls-n__sub calls-n__system">
                        <span class="calls-n__bend" :title="$t(`telephony.call_types.${item.call_type}`)">
                            <svg
                                v-if="item.call_type === 'incoming'"
                                width="14" height="14" viewBox="0 0 14 14" fill="none"
                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                            >
                                <path d="M3 1.5v5.5a3 3 0 0 0 3 3h6"/>
                                <path d="M9.5 7.5l2.5 2.5-2.5 2.5"/>
                            </svg>
                            <svg
                                v-else
                                width="14" height="14" viewBox="0 0 14 14" fill="none"
                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                            >
                                <path d="M12 10H6a3 3 0 0 1-3-3V1.5"/>
                                <path d="M0.5 4L3 1.5 5.5 4"/>
                            </svg>
                        </span>
                        {{ item.number }}
                    </span>
                </span>

                <!-- stretched, not centred: a centred line never gets narrower than
                     its own text, so the long id would spill over the player -->
                <span class="calls-n__dur">
                    <span class="calls-n__date">{{ getDuration(item) }}</span>
                    <span v-if="item.status === ANSWERED" class="calls-n__sub calls-n__sip">
                        <span class="calls-n__sip-label">{{ $t('telephony.calls_history.columns.5') }}</span>
                        <BaseFadeScroll class="calls-n__sip-id" :text="item.extension"/>
                    </span>
                </span>

                <AudioPlayer v-if="item.recording_url" class="calls-n__player" :src="item.recording_url" :download-name="recordName(item)"/>
                <span v-else class="calls-n__none">—</span>
            </li>

            <li class="calls-n__info">
                <slot name="information_column"/>
            </li>
            <li class="scroll-fade" :class="{ 'is-bottom': isBottom || loading }" aria-hidden="true"></li>
        </ul>
    </div>
</template>

<style scoped lang="scss">
@use "@/styles/scroll-fade.scss";
// the desktop table with seven columns folded into five, for 900–1199px
.calls-n {
    // the minimums add up to ~710px; 760px is left at a 900px window
    --columns: minmax(90px, 0.7fr) minmax(120px, 0.9fr) minmax(170px, 1.3fr) minmax(90px, 0.6fr) minmax(170px, 1.2fr);

    &__head,
    &__row {
        display: grid;
        grid-template-columns: var(--columns);
        gap: 10px;
        align-items: center;
        padding: 0 16px;
        text-align: center;
    }

    // thead of table.scss
    &__head {
        margin: 5px 0 2px;
        padding-top: 20px;
        padding-bottom: 13px;
        border-radius: 11px;
        background-color: var(--block-color);
    }

    &__list {
        max-height: 293px;
        overflow-y: auto;
        margin: 5px 0;
        padding: 0;
        list-style: none;
    }

    &__row {
        padding-top: 9px;
        padding-bottom: 9px;
        border-bottom: 1px solid var(--block-color);
        border-radius: 10px;
        transition: background-color 0.3s ease;

        > * {
            justify-self: center;
        }

        &:hover {
            background-color: #f4f3fc85;
        }
    }

    &__when,
    &__dur {
        display: flex;
        flex-direction: column;
        line-height: 1.25;
    }

    &__date {
        color: #212529;
        font-size: 15px;
    }

    &__sub {
        color: var(--text-light-color);
        font-size: 13px;
    }

    &__pair {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
    }

    // nested to outweigh the centring in CallNumberView's own scoped styles
    &__pair &__client {
        justify-content: flex-start;
        gap: 6px;
        font-size: 15px;
        white-space: nowrap;
        color: #212529;
    }

    &__system {
        display: flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;
    }

    &__bend {
        display: flex;
        color: var(--border-light-color);
    }

    &__row > &__dur {
        justify-self: stretch;
        min-width: 0;
    }

    &__sip {
        display: flex;
        justify-content: center;
        gap: 4px;
        min-width: 0;
    }

    &__sip-label {
        flex-shrink: 0;
    }

    &__sip-id {
        flex: 0 1 auto;
        font-variant-numeric: tabular-nums;
    }

    &__row > &__player,
    &__row > &__none {
        justify-self: stretch;
    }

    &__none {
        color: var(--border-light-color);
    }

    &__info {
        display: block;
    }
}

.scroll-fade {
    @include scroll-fade.scroll-fade;
}

.headed_text {
    font-family: Gilroy;
    font-weight: 500;
    line-height: 1.3;
    text-transform: uppercase;
    color: var(--text-light-color);
}
</style>
